import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import { CHART_COLORS, tensionBySteps, TENSION_OPACITY_BY_LEVEL } from "../helpers/chart"

Chart.register(...registerables)

export default class extends Controller {
  static targets = ["canvas", "container", "typeButton"]
  static values = { data: Object }

  connect() {
    this._currentType = "radar"
    this._syncButtonState("radar")
    this._buildChart("radar")
    this.onWindowResize = this._debounce(() => this._resizeChart(), 150)
    window.addEventListener("resize", this.onWindowResize)
    if (this.hasContainerTarget) {
      this._resizeObserver = new ResizeObserver(() => this._resizeChart())
      this._resizeObserver.observe(this.containerTarget)
    }
  }

  switchChart(event) {
    const type = event.params.type
    if (type === this._currentType) return

    this._currentType = type
    this._syncButtonState(type)

    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    this._buildChart(type)
    this._resizeChart()
  }

  _syncButtonState(type) {
    this.typeButtonTargets.forEach((btn) => {
      const active = btn.dataset.pairAnalysisChartTypeParam === type
      btn.classList.toggle("is-info", active)
      btn.classList.toggle("is-selected", active)
      btn.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  disconnect() {
    window.removeEventListener("resize", this.onWindowResize)
    this._resizeObserver?.disconnect()
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  _resizeChart() {
    if (this.chart) this.chart.resize()
  }

  _debounce(fn, wait) {
    let timeoutId
    return (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  // ─── Chart builders ────────────────────────────────────────────────────────

  _buildChart(type) {
    let data = this.dataValue
    if (typeof data === "string") {
      const decoded = data.replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      try {
        data = JSON.parse(decoded)
      } catch {
        return
      }
    }
    if (!data?.scale_order?.length || !data?.scores_a?.length || !data?.scores_b?.length) return

    if (type === "line") this.chart = this._buildLineChart(data)
    else if (type === "bar") this.chart = this._buildBarChart(data)
    else if (type === "radar") this.chart = this._buildRadarChart(data)
  }

  _buildLineChart({ scale_order, scale_labels, scores_a, scores_b, coefficients, thresholds, y_max, label_a, label_b }) {
    const labels = scale_labels || scale_order
    const yAxisMax = y_max ?? 24
    const intersectionData = this._detectIntersections(scores_a, scores_b)
    const ptColorsA = this._tensionColorsBySteps(scores_a, scores_b, coefficients, CHART_COLORS.primary)
    const ptColorsB = this._tensionColorsBySteps(scores_a, scores_b, coefficients, CHART_COLORS.success)
    const nanData = new Array(scores_a.length).fill(NaN)

    return new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: label_a,
            data: scores_a,
            borderColor: CHART_COLORS.primary,
            backgroundColor: CHART_COLORS.primary,
            pointBackgroundColor: ptColorsA,
            pointBorderColor: ptColorsA,
            tension: 0,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            order: 1
          },
          {
            label: label_b,
            data: scores_b,
            borderColor: CHART_COLORS.success,
            backgroundColor: CHART_COLORS.success,
            pointBackgroundColor: ptColorsB,
            pointBorderColor: ptColorsB,
            tension: 0,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            order: 2
          },
          {
            label: "Верхняя граница нормы",
            data: nanData,
            borderColor: "rgba(160, 100, 210, 0.85)",
            backgroundColor: "rgba(160, 100, 210, 0.85)",
            borderDash: [4, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            order: 3
          },
          {
            label: "Нижняя граница нормы",
            data: nanData,
            borderColor: "rgba(100, 149, 237, 0.85)",
            backgroundColor: "rgba(100, 149, 237, 0.85)",
            borderDash: [2, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            order: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: this._legendConfig(),
          tooltip: this._tooltipConfig(scores_a, scores_b, intersectionData, coefficients)
        },
        scales: {
          y: { beginAtZero: true, max: yAxisMax, ticks: { stepSize: 6, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
          x: { ticks: { font: { size: 11 } }, grid: { color: "rgba(0,0,0,0.06)" } }
        },
        layout: { padding: { top: 4, bottom: 0, left: 0, right: 4 } }
      },
      plugins: [
        this._buildBackgroundPlugin(scale_order, thresholds),
        this._buildTensionOverlayPlugin(scores_a, scores_b, coefficients),
        this._buildNormLinesPlugin(scale_order, thresholds),
        this._buildIntersectionDotsPlugin(intersectionData)
      ]
    })
  }

  _buildBarChart({ scale_order, scale_labels, scores_a, scores_b, coefficients, thresholds, y_max, label_a, label_b }) {
    const labels = scale_labels || scale_order
    const yAxisMax = y_max ?? 24
    const intersectionData = this._detectIntersections(scores_a, scores_b)
    // Dots float just above the tallest bar at each scale
    const dotYData = intersectionData.map((v, i) =>
      v !== null ? Math.max(scores_a[i], scores_b[i]) + 0.8 : null
    )
    const nanData = new Array(scores_a.length).fill(NaN)

    return new Chart(this.canvasTarget, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: label_a,
            data: scores_a,
            backgroundColor: CHART_COLORS.primary + "bb",
            borderColor: CHART_COLORS.primary,
            borderWidth: 1,
            order: 1
          },
          {
            label: label_b,
            data: scores_b,
            backgroundColor: CHART_COLORS.success + "bb",
            borderColor: CHART_COLORS.success,
            borderWidth: 1,
            order: 2
          },
          {
            label: "Верхняя граница нормы",
            type: "line",
            data: nanData,
            borderColor: "rgba(160, 100, 210, 0.85)",
            backgroundColor: "rgba(160, 100, 210, 0.85)",
            borderDash: [4, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            order: 3
          },
          {
            label: "Нижняя граница нормы",
            type: "line",
            data: nanData,
            borderColor: "rgba(100, 149, 237, 0.85)",
            backgroundColor: "rgba(100, 149, 237, 0.85)",
            borderDash: [2, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            order: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: this._legendConfig(),
          tooltip: this._tooltipConfig(scores_a, scores_b, intersectionData, coefficients)
        },
        scales: {
          y: { beginAtZero: true, max: yAxisMax, ticks: { stepSize: 6, font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.06)" } },
          x: { ticks: { font: { size: 11 } }, grid: { color: "rgba(0,0,0,0.06)" } }
        },
        layout: { padding: { top: 4, bottom: 0, left: 0, right: 4 } }
      },
      plugins: [
        this._buildBackgroundPlugin(scale_order, thresholds),
        this._buildTensionOverlayPlugin(scores_a, scores_b, coefficients),
        this._buildNormLinesPlugin(scale_order, thresholds),
        this._buildIntersectionDotsPlugin(dotYData)
      ]
    })
  }

  _buildRadarChart({ scale_order, scale_labels, scores_a, scores_b, coefficients, thresholds, y_max, label_a, label_b }) {
    const labels = scale_labels || scale_order
    const yAxisMax = y_max ?? 24
    const intersectionData = this._detectIntersections(scores_a, scores_b)
    const ptColorsA = this._tensionColorsBySteps(scores_a, scores_b, coefficients, CHART_COLORS.primary)
    const ptColorsB = this._tensionColorsBySteps(scores_a, scores_b, coefficients, CHART_COLORS.success)
    const normMax = scale_order.map((code) => thresholds[code].norm_max)
    const normMin = scale_order.map((code) => thresholds[code].norm_min)

    return new Chart(this.canvasTarget, {
      type: "radar",
      data: {
        labels,
        datasets: [
          {
            label: label_a,
            data: scores_a,
            borderColor: CHART_COLORS.primary,
            backgroundColor: "rgba(50,115,220,0.12)",
            pointBackgroundColor: ptColorsA,
            pointBorderColor: ptColorsA,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            order: 1
          },
          {
            label: label_b,
            data: scores_b,
            borderColor: CHART_COLORS.success,
            backgroundColor: "rgba(35,209,96,0.12)",
            pointBackgroundColor: ptColorsB,
            pointBorderColor: ptColorsB,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            order: 2
          },
          {
            label: "Верхняя граница нормы",
            data: normMax,
            borderColor: "rgba(160,100,210,0.55)",
            backgroundColor: "rgba(160,100,210,0.06)",
            borderDash: [4, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 0,
            order: 3
          },
          {
            label: "Нижняя граница нормы",
            data: normMin,
            borderColor: "rgba(100,149,237,0.55)",
            backgroundColor: "rgba(100,149,237,0.06)",
            borderDash: [2, 3],
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 0,
            order: 4
          },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: this._legendConfig(),
          tooltip: this._tooltipConfig(scores_a, scores_b, intersectionData, coefficients)
        },
        scales: {
          r: {
            min: 0,
            max: yAxisMax,
            ticks: { stepSize: 6, font: { size: 9 }, backdropColor: "transparent" },
            pointLabels: { font: { size: 11 } },
            grid: { color: "rgba(0,0,0,0.08)" }
          }
        }
      },
      plugins: [this._buildRadarIntersectionDotsPlugin(intersectionData)]
    })
  }

  // ─── Shared config helpers ─────────────────────────────────────────────────

  _legendConfig() {
    return {
      position: "bottom",
      labels: { boxWidth: 12, padding: 8, font: { size: 11 } }
    }
  }

  _tooltipConfig(scores_a, scores_b, intersectionData, coefficients) {
    return {
      padding: 6,
      bodyFont: { size: 11 },
      titleFont: { size: 11 },
      callbacks: {
        afterBody: (tooltipItems) => {
          const idx = tooltipItems[0]?.dataIndex
          if (idx == null) return []
          const lines = []
          if (intersectionData[idx] !== null) lines.push("● пересечение")
          const diff = Math.abs(scores_a[idx] - scores_b[idx])
          const steps = diff / coefficients[idx]
          const level = tensionBySteps(steps)
          if (level !== "none") lines.push("⚠ зона напряжения")
          return lines
        }
      }
    }
  }

  // Returns per-scale color by steps: 0–2 default, 3–6 weak red, 7+ strong red. Steps = diff / coefficient.
  _tensionColorsBySteps(scores_a, scores_b, coefficients, defaultColor) {
    return scores_a.map((_, i) => {
      const diff = Math.abs(scores_a[i] - scores_b[i])
      const steps = diff / coefficients[i]
      const level = tensionBySteps(steps)
      if (level === "none") return defaultColor
      if (level === "weak") return CHART_COLORS.tensionWeak
      return CHART_COLORS.tension
    })
  }

  // ─── Plugins ───────────────────────────────────────────────────────────────

  // Dashed horizontal lines at norm_min (lower) and norm_max (upper) for each scale column
  _buildNormLinesPlugin(scale_order, thresholds) {
    return {
      id: "pairAnalysisNormLines",
      afterDraw(chart) {
        const { ctx, chartArea, scales } = chart
        if (!chartArea) return

        const xScale = scales.x
        const yScale = scales.y

        ctx.save()
        ctx.beginPath()
        ctx.rect(chartArea.left, chartArea.top, chartArea.width, chartArea.height)
        ctx.clip()

        scale_order.forEach((code, i) => {
          const t = thresholds[code]
          if (!t) return

          const colLeft = i === 0
            ? chartArea.left
            : (xScale.getPixelForValue(i - 1) + xScale.getPixelForValue(i)) / 2

          const colRight = i === scale_order.length - 1
            ? chartArea.right
            : (xScale.getPixelForValue(i) + xScale.getPixelForValue(i + 1)) / 2

          const yNormMax = yScale.getPixelForValue(t.norm_max)
          ctx.beginPath()
          ctx.setLineDash([4, 4])
          ctx.strokeStyle = "rgba(160, 100, 210, 0.85)"
          ctx.lineWidth = 1.5
          ctx.moveTo(colLeft, yNormMax)
          ctx.lineTo(colRight, yNormMax)
          ctx.stroke()

          const yNormMin = yScale.getPixelForValue(t.norm_min)
          ctx.beginPath()
          ctx.setLineDash([2, 4])
          ctx.strokeStyle = "rgba(100, 149, 237, 0.85)"
          ctx.lineWidth = 1.5
          ctx.moveTo(colLeft, yNormMin)
          ctx.lineTo(colRight, yNormMin)
          ctx.stroke()
        })

        ctx.restore()
      }
    }
  }

  // ─── Intersection marker plugins ───────────────────────────────────────────

  // Draws a 5-pointed star at (cx, cy) with outer radius.
  _drawStar(ctx, cx, cy, outerRadius) {
    const innerRadius = outerRadius * 0.4
    const points = 5
    ctx.beginPath()
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius
      const angle = (i * Math.PI) / points - Math.PI / 2
      const x = cx + Math.cos(angle) * radius
      const y = cy + Math.sin(angle) * radius
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
  }

  // Draws gold stars on line/bar charts at intersection positions.
  _buildIntersectionDotsPlugin(markerData) {
    const drawStar = this._drawStar.bind(this)
    return {
      id: "pairAnalysisIntersectionDots",
      afterDatasetsDraw(chart) {
        const { ctx, scales } = chart
        const xScale = scales.x
        const yScale = scales.y
        if (!xScale || !yScale) return

        ctx.save()
        ctx.shadowColor = "rgba(255,255,255,0.85)"
        ctx.shadowBlur = 3

        markerData.forEach((yValue, i) => {
          if (yValue === null) return
          const x = xScale.getPixelForValue(i)
          const y = yScale.getPixelForValue(yValue)
          drawStar(ctx, x, y, 9)
          ctx.fillStyle = CHART_COLORS.intersection
          ctx.fill()
          ctx.strokeStyle = "white"
          ctx.lineWidth = 1.5
          ctx.stroke()
        })

        ctx.restore()
      }
    }
  }

  // Same but uses scales.r polar coordinates for radar chart.
  _buildRadarIntersectionDotsPlugin(intersectionData) {
    const drawStar = this._drawStar.bind(this)
    return {
      id: "pairAnalysisRadarIntersectionDots",
      afterDatasetsDraw(chart) {
        const { ctx, scales } = chart
        const rScale = scales.r
        if (!rScale) return

        ctx.save()
        ctx.shadowColor = "rgba(255,255,255,0.85)"
        ctx.shadowBlur = 3

        intersectionData.forEach((v, i) => {
          if (v === null) return
          const pixelDist = rScale.getDistanceFromCenterForValue(v)
          const pos = rScale.getPointPosition(i, pixelDist)
          drawStar(ctx, pos.x, pos.y, 9)
          ctx.fillStyle = CHART_COLORS.intersection
          ctx.fill()
          ctx.strokeStyle = "white"
          ctx.lineWidth = 1.5
          ctx.stroke()
        })

        ctx.restore()
      }
    }
  }

  // Per-column background zones for line and bar charts
  _buildBackgroundPlugin(scale_order, thresholds) {
    return {
      id: "pairAnalysisBackgroundZones",
      beforeDraw(chart) {
        const { ctx, chartArea, scales } = chart
        if (!chartArea) return

        const xScale = scales.x
        const yScale = scales.y

        ctx.save()
        ctx.beginPath()
        ctx.rect(chartArea.left, chartArea.top, chartArea.width, chartArea.height)
        ctx.clip()

        scale_order.forEach((code, i) => {
          const t = thresholds[code]
          if (!t) return

          const colLeft = i === 0
            ? chartArea.left
            : (xScale.getPixelForValue(i - 1) + xScale.getPixelForValue(i)) / 2

          const colRight = i === scale_order.length - 1
            ? chartArea.right
            : (xScale.getPixelForValue(i) + xScale.getPixelForValue(i + 1)) / 2

          // Blue gradient: transparent at norm_min → solid at bottom
          const blueTop = yScale.getPixelForValue(t.norm_min)
          const blueBottom = yScale.getPixelForValue(0)
          const blueGrad = ctx.createLinearGradient(0, blueTop, 0, blueBottom)
          blueGrad.addColorStop(0, "rgba(100, 149, 237, 0)")
          blueGrad.addColorStop(1, "rgba(100, 149, 237, 0.35)")
          ctx.fillStyle = blueGrad
          ctx.fillRect(colLeft, blueTop, colRight - colLeft, blueBottom - blueTop)

          // Lilac gradient: transparent at norm_max → solid at top
          const lilacTop = yScale.getPixelForValue(t.max_score)
          const lilacBottom = yScale.getPixelForValue(t.norm_max)
          const lilacGrad = ctx.createLinearGradient(0, lilacBottom, 0, lilacTop)
          lilacGrad.addColorStop(0, "rgba(160, 100, 210, 0)")
          lilacGrad.addColorStop(1, "rgba(160, 100, 210, 0.35)")
          ctx.fillStyle = lilacGrad
          ctx.fillRect(colLeft, lilacTop, colRight - colLeft, lilacBottom - lilacTop)
        })

        ctx.restore()
      }
    }
  }

  // Red gradient overlay for line/bar columns. Opacity by steps: 0–2 none, 3–6 weak, 7+ strong. Steps = diff / coefficient.
  _buildTensionOverlayPlugin(scores_a, scores_b, coefficients) {
    return {
      id: "pairAnalysisTensionOverlay",
      beforeDraw(chart) {
        const { ctx, chartArea, scales } = chart
        if (!chartArea) return

        const xScale = scales.x
        const yScale = scales.y
        if (!xScale || !yScale) return

        ctx.save()
        ctx.beginPath()
        ctx.rect(chartArea.left, chartArea.top, chartArea.width, chartArea.height)
        ctx.clip()

        for (let i = 0; i < scores_a.length; i++) {
          const diff = Math.abs(scores_a[i] - scores_b[i])
          const steps = diff / coefficients[i]
          const level = tensionBySteps(steps)
          const opacity = TENSION_OPACITY_BY_LEVEL[level]
          if (opacity === 0) continue

          const colLeft = i === 0
            ? chartArea.left
            : (xScale.getPixelForValue(i - 1) + xScale.getPixelForValue(i)) / 2

          const colRight = i === scores_a.length - 1
            ? chartArea.right
            : (xScale.getPixelForValue(i) + xScale.getPixelForValue(i + 1)) / 2

          const yTop = chartArea.top
          const yBottom = chartArea.bottom

          const grad = ctx.createLinearGradient(0, yTop, 0, yBottom)
          grad.addColorStop(0, "rgba(229, 62, 62, 0)")
          grad.addColorStop(1, `rgba(229, 62, 62, ${opacity})`)
          ctx.fillStyle = grad
          ctx.fillRect(colLeft, yTop, colRight - colLeft, yBottom - yTop)
        }

        ctx.restore()
      }
    }
  }

  // ─── Intersection detection ────────────────────────────────────────────────

  // Returns Y-value for golden dot only when both partners have equal scores at that scale.
  _detectIntersections(scores_a, scores_b) {
    const n = scores_a.length
    const result = new Array(n).fill(null)

    for (let i = 0; i < n; i++) {
      if (scores_a[i] === scores_b[i]) {
        result[i] = scores_a[i]
      }
    }

    return result
  }
}
